/**
 * Session store.
 *
 * Persisted to localStorage so a refresh does not log the driver out mid-paddock.
 * The store registers itself with the API client on module init, which is what
 * lets `api.ts` attach and silently refresh tokens without importing zustand.
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { limparCacheDaApi } from './pwa';
import type { AuthResponse, AuthTokens, RegisterRequest, User } from '@mychrono/contracts';
import { api, configureApiAuth, isApiError } from './api';

export const AUTH_STORAGE_KEY = 'mychrono.auth';

interface AuthPersisted {
  user: User | null;
  tokens: AuthTokens | null;
  /** Epoch ms at which the access token stops being accepted. */
  expiresAt: number | null;
}

export interface AuthState extends AuthPersisted {
  /** False until localStorage has been read; guards render a loader on it. */
  hydrated: boolean;
  /** True while a login/register/logout round trip is in flight. */
  pending: boolean;

  login: (email: string, password: string) => Promise<User>;
  register: (payload: RegisterRequest) => Promise<User>;
  logout: () => Promise<void>;
  /** Forces a token refresh. Returns null when the session is gone. */
  refresh: () => Promise<AuthTokens | null>;
  /** Re-reads the user from the server; called once on boot. */
  revalidate: () => Promise<User | null>;
  /**
   * Installs a session that was obtained outside the store — the Google callback
   * screen already holds the user and tokens by the time it gets here, so it has
   * nothing to log in with.
   */
  adopt: (response: AuthResponse) => void;
  setUser: (user: User) => void;
  clear: () => void;
}

function expiryFrom(tokens: AuthTokens): number {
  return Date.now() + Math.max(0, tokens.expiresIn - 30) * 1000;
}

export const useAuth = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      tokens: null,
      expiresAt: null,
      hydrated: false,
      pending: false,

      login: async (email, password) => {
        set({ pending: true });
        try {
          const response = await api.auth.login({ email, password });
          set({
            user: response.user,
            tokens: response.tokens,
            expiresAt: expiryFrom(response.tokens),
            pending: false,
          });
          return response.user;
        } catch (error) {
          set({ pending: false });
          throw error;
        }
      },

      register: async (payload) => {
        set({ pending: true });
        try {
          const response = await api.auth.register(payload);
          set({
            user: response.user,
            tokens: response.tokens,
            expiresAt: expiryFrom(response.tokens),
            pending: false,
          });
          return response.user;
        } catch (error) {
          set({ pending: false });
          throw error;
        }
      },

      logout: async () => {
        const refreshToken = get().tokens?.refreshToken ?? null;
        set({ pending: true });
        try {
          if (refreshToken) await api.auth.logout(refreshToken);
        } catch {
          /* A failed logout still ends the session on this device. */
        } finally {
          // The service worker keeps API responses for offline use. Left behind,
          // the next person to open this device offline would see the previous
          // driver's sessions and lap times.
          limparCacheDaApi();
          set({ user: null, tokens: null, expiresAt: null, pending: false });
        }
      },

      refresh: async () => {
        const refreshToken = get().tokens?.refreshToken;
        if (!refreshToken) return null;
        try {
          const tokens = await api.auth.refresh(refreshToken);
          set({ tokens, expiresAt: expiryFrom(tokens) });
          return tokens;
        } catch {
          set({ user: null, tokens: null, expiresAt: null });
          return null;
        }
      },

      revalidate: async () => {
        if (!get().tokens) return null;
        try {
          const user = await api.auth.me();
          set({ user });
          return user;
        } catch (error) {
          if (isApiError(error) && error.status === 401) {
            set({ user: null, tokens: null, expiresAt: null });
          }
          return null;
        }
      },

      adopt: (response) =>
        set({
          user: response.user,
          tokens: response.tokens,
          expiresAt: expiryFrom(response.tokens),
          pending: false,
        }),

      setUser: (user) => set({ user }),

      clear: () => set({ user: null, tokens: null, expiresAt: null }),
    }),
    {
      name: AUTH_STORAGE_KEY,
      version: 1,
      storage: createJSONStorage(() => localStorage),
      partialize: (state): AuthPersisted => ({
        user: state.user,
        tokens: state.tokens,
        expiresAt: state.expiresAt,
      }),
    },
  ),
);

/**
 * Flips the guards from "restoring" to "decided", then confirms the restored
 * user against the server. This must NOT live in `onRehydrateStorage`: with a
 * synchronous storage backend that callback runs while `useAuth` is still in the
 * temporal dead zone, and the resulting ReferenceError is swallowed by zustand —
 * leaving every route guard stuck on its loading state forever.
 */
function markHydrated(): void {
  useAuth.setState({ hydrated: true });
  if (useAuth.getState().tokens) void useAuth.getState().revalidate();
}

if (useAuth.persist.hasHydrated()) {
  markHydrated();
} else {
  useAuth.persist.onFinishHydration(() => markHydrated());
}

configureApiAuth({
  getAccessToken: () => useAuth.getState().tokens?.accessToken ?? null,
  getRefreshToken: () => useAuth.getState().tokens?.refreshToken ?? null,
  onTokensRefreshed: (tokens) =>
    useAuth.setState({ tokens, expiresAt: Date.now() + Math.max(0, tokens.expiresIn - 30) * 1000 }),
  onAuthLost: () => useAuth.setState({ user: null, tokens: null, expiresAt: null }),
});

// ---------------------------------------------------------------------------
// Selectors
// ---------------------------------------------------------------------------

export const useUser = (): User | null => useAuth((state) => state.user);
export const useIsAuthenticated = (): boolean =>
  useAuth((state) => Boolean(state.user && state.tokens));
export const useIsAdmin = (): boolean => useAuth((state) => state.user?.role === 'admin');
export const useAuthHydrated = (): boolean => useAuth((state) => state.hydrated);

/** Non-reactive read, for code outside React. */
export function currentUser(): User | null {
  return useAuth.getState().user;
}
